// src/component/ButtonBox/ButtonBox.tsx
import React, { useState } from "react";
import { Button } from "primereact/button";
import { IButtonBoxProps } from "../interface/Button";
import { useTranslation } from "react-i18next";

const ButtonBox = (props: IButtonBoxProps) => {
  const {
    label = "",
    shape = "square",
    size = "medium",
    state = "default",
    loading = false,
    disabled = false,
    icon,
    iconPos = "left",
    onClick,
    onClickWithLoader,
    type = "button",
    shimmer = false,
    ...rest
  } = props;

  const { t } = useTranslation();
  const translatedLabel = t(label);
  const isDisabled = state === "disabled" || disabled;
  const isLoading = state === "loader" || loading;

  // Local loading state for onClickWithLoader
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClickWithLoader) {
      setIsLocalLoading(true);
      try {
        await onClickWithLoader(e);
      } finally {
        setIsLocalLoading(false);
      }
    } else if (onClick) {
      onClick(e);
    }
  };

  const getButtonStyles = () => {
    const base = `
      font-semibold inline-flex items-center gap-xs justify-center w-full
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 
      ${shimmer ? "group relative overflow-hidden" : ""}
      ${shape === "round" ? "rounded-full" : "rounded-md"}
    `;

    const sizeStyles = {
      xSmall: "text-label-small font-medium h-lg px-sm py-3xs",
      small: "text-label-medium font-semibold h-xl px-sm py-xs",
      medium: "text-label-medium font-semibold h-2xl px-sm py-xs",
      large: "text-label-large font-semibold h-3xl px-md py-xs",
      xLarge: "text-label-large h-4xl px-lg py-sm",
    }[size];

    const variant = props.variant ?? "primary";

    const variantStyles = {
      primary: `bg-black text-text-inverse-primary`,
      secondary: `
        border border-border-brand text-text-brand bg-transparent
        hover:border-border-brand-hover hover:text-text-brand-hover hover:bg-surface-primary-hover
        active:bg-surface-brand-pressed active:text-text-inverse-primary
        focus-visible:ring-border-brand-focus
      `,
      tertiary: `
        bg-transparent text-text-brand
        hover:text-text-brand-hover hover:bg-surface-primary-hover
        active:text-text-brand-hover
        focus-visible:ring-border-brand-focus
      `,
      link: isDisabled
        ? `text-sm text-gray-400 underline cursor-not-allowed transition-colors duration-200 !p-0`
        : `text-sm text-[#3B455E] underline transition-colors duration-200 hover:text-blue-800 focus:outline-none !p-0`,
    };

    if (isDisabled) {
      return `
        ${base} ${sizeStyles}
        bg-surface-disabled text-text-disabled cursor-not-allowed
      `
        .trim()
        .replace(/\s+/g, " ");
    }

    if (isLoading || isLocalLoading) {
      return `
        ${base} ${sizeStyles} ${variantStyles[variant]}
         cursor-wait
      `
        .trim()
        .replace(/\s+/g, " ");
    }

    return `${base} ${sizeStyles} ${variantStyles[variant]}`.trim().replace(/\s+/g, " ");
  };

  return (
    <Button
      {...rest}
      className={`${getButtonStyles()} ${props.className ?? ""}`.trim()}
      label={translatedLabel}
      icon={icon}
      iconPos={iconPos}
      loading={isLoading || isLocalLoading}
      disabled={isDisabled}
      onClick={handleClick}
      type={type}
    >
      {shimmer && (
        <div className="pointer-events-none absolute -top-[50px] -left-[75px] h-[155px] w-[50px] rotate-[35deg] bg-white/40 transition-all duration-300 ease-out group-hover:left-[120%]" />
      )}
    </Button>
  );
};

export default React.memo(ButtonBox);
